import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * EVG — helper della scheda prodotto: promessa di consegna, misura anelli, concierge.
 * Ogni elemento si riallinea alla variante scelta copiando la propria versione
 * dall'HTML che il variant picker scarica (stesso schema di product-inventory.js).
 */

/**
 * Publishes a custom storefront event, readable from a Customer Events custom pixel.
 * @param {string} name
 * @param {Record<string, unknown>} payload
 */
function track(name, payload) {
  try {
    // @ts-ignore Shopify global
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch (error) {
    // L'analisi non deve mai bloccare la pagina.
  }
}

/**
 * Element that re-renders its children from the variant update payload.
 * Markup contract: `data-evg-sync="<block id>"` and `data-product-id`.
 */
class EvgVariantSync extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#controller.signal,
    });
    this.refresh();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newElement = event.detail.data.html?.querySelector(`[data-evg-sync="${this.dataset.evgSync}"]`);
    if (!newElement) return;

    morph(this, newElement, { childrenOnly: true });
    this.refresh();
  };

  /** Hook for subclasses, called after first render and after each variant change. */
  refresh() {}
}

/**
 * Promessa di consegna: riempie le date con il calendario del visitatore.
 * I dati stanno su un figlio `[data-evg-delivery]` (il morph aggiorna solo i figli):
 * mode (ready|made), readyDays, madeDays, batchWeekday (0-6, -1 = nessuna fusione fissa).
 */
class EvgDeliveryPromise extends EvgVariantSync {
  refresh() {
    const data = this.querySelector('[data-evg-delivery]');
    if (!(data instanceof HTMLElement)) return;

    const { mode, readyDays, madeDays, batchWeekday } = data.dataset;
    const today = new Date();
    const lang = document.documentElement.lang || 'it';

    const longDate = new Intl.DateTimeFormat(lang, { weekday: 'long', day: 'numeric', month: 'long' });
    const shortDate = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long' });

    let benchDate = today;
    const weekday = Number(batchWeekday);
    if (mode === 'made' && weekday >= 0) benchDate = nextWeekday(today, weekday);

    const arrival =
      mode === 'ready'
        ? addBusinessDays(today, Number(readyDays) || 3)
        : addBusinessDays(benchDate, Number(madeDays) || 18);

    /** @type {Record<string, string>} */
    const values = {
      arrival: shortDate.format(arrival),
      bench: longDate.format(benchDate),
      closing: longDate.format(addDays(benchDate, -1)),
    };

    for (const slot of this.querySelectorAll('[data-evg-date]')) {
      const key = slot.getAttribute('data-evg-date') ?? '';
      if (values[key]) slot.textContent = values[key];
    }

    this.#markReady(data.dataset);
  }

  /** @type {{ id: string, stock: number, made: string } | null} */
  #ready = null;

  /** @type {AbortController | undefined} */
  #readyController;

  connectedCallback() {
    this.#readyController = new AbortController();
    const { signal } = this.#readyController;
    const scope = this.closest('.shopify-section, dialog');
    // La quantità cambia: l'id giusto è già nel form quando il tema lo legge (lo legge all'invio, prima di noi).
    scope?.addEventListener(ThemeEvents.quantitySelectorUpdate, () => this.#applyReady(), { signal });
    scope?.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') this.#applyReady();
    }, { signal });
    // Carrello cambiato (aggiunta, rimozione dal carrello laterale): la giacenza rimasta la sa il Liquid,
    // quindi si ridisegna il blocco. Nel frattempo, se è appena entrato il pezzo pronto, si scala a mano.
    document.addEventListener(
      ThemeEvents.cartUpdate,
      (event) => {
        const detail = /** @type {CustomEvent} */ (event).detail;
        if (detail?.data?.didError) return;
        if (this.#ready && String(detail?.sourceId) === this.#ready.id) {
          this.#ready.stock -= Number(detail.data?.itemCount) || 1;
          this.#applyReady();
        }
        this.#reloadAfterCart();
      },
      { signal }
    );
    super.connectedCallback();
  }

  /** Riscarica il blocco con il carrello aggiornato (stessa richiesta del variant picker). */
  async #reloadAfterCart() {
    if (!this.querySelector('script[data-evg-ready], [data-ready-variant]')) return;
    const sectionId = this.closest('.shopify-section')?.id.replace(/^shopify-section-/, '');
    const variantId = this.querySelector('[data-evg-delivery]')?.getAttribute('data-variant-id');
    const url = this.dataset.productUrl;
    if (!sectionId || !variantId || !url) return;
    try {
      const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}variant=${variantId}&section_id=${sectionId}`);
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector(`[data-evg-sync="${this.dataset.evgSync}"]`);
      // Se intanto è cambiata la variante, vale il ridisegno del variant picker.
      if (!fresh || this.querySelector('[data-evg-delivery]')?.getAttribute('data-variant-id') !== variantId) return;
      morph(this, fresh, { childrenOnly: true });
      this.refresh();
    } catch (error) {
      // Senza rete il blocco resta com'è: al prossimo cambio di variante si riallinea.
    }
  }

  disconnectedCallback() {
    this.#readyController?.abort();
    super.disconnectedCallback();
  }

  /**
   * Segna nel selettore i valori che, con le altre scelte, portano a un pezzo già pronto (scheda gemella
   * "-ready-to-ship") e, se la scelta attuale è pronta, fa mettere nel carrello quel pezzo al posto del
   * su misura: stesso prezzo, e la giacenza scala dalla scheda giusta.
   * @param {DOMStringMap} panel - readyVariant, readyStock, madeVariant (vuoti se la scelta non è pronta)
   */
  #markReady(panel) {
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;

    /** @type {{ label?: string, combos?: ReadyCombo[] }} */
    let ready = {};
    try {
      ready = JSON.parse(this.querySelector('script[data-evg-ready]')?.textContent || '{}');
    } catch (error) {
      ready = {};
    }

    const picker = section.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (picker) markReadyOptions(picker, ready.combos ?? [], ready.label ?? '');

    this.#ready =
      panel.readyVariant && panel.madeVariant
        ? { id: panel.readyVariant, stock: Number(panel.readyStock) || 0, made: panel.madeVariant }
        : null;
    // Il form del carrello riscrive il proprio id sullo stesso evento: il nostro va dopo.
    if (this.#ready) queueMicrotask(() => this.#applyReady());
  }

  /** Pezzo pronto se ne restano abbastanza per la quantità scelta, altrimenti il su misura. */
  #applyReady() {
    if (!this.#ready) return;
    const form = this.closest('.shopify-section, dialog')?.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"]`
    );
    const input = form?.querySelector('input[name="id"]');
    if (!(input instanceof HTMLInputElement)) return;
    const quantityInput = form?.querySelector('input[name="quantity"]');
    const quantity = quantityInput instanceof HTMLInputElement ? Number(quantityInput.value) || 1 : 1;
    input.value = quantity <= this.#ready.stock ? this.#ready.id : this.#ready.made;
  }
}

/** @typedef {{ id: number, values: string[] }} ReadyCombo */

const READY_SEPARATOR = ' – ';

/**
 * Aggiunge (o toglie) la dicitura ai valori del selettore che completano una combinazione pronta.
 * Il variant picker si ridisegna a ogni cambio, quindi si riparte sempre dal testo originale.
 * @param {Element} picker
 * @param {ReadyCombo[]} combos
 * @param {string} label
 */
function markReadyOptions(picker, combos, label) {
  /** @type {{ value: string, holder: HTMLElement | null }[][]} */
  const groups = [];
  /** @type {string[]} */
  const chosen = [];

  for (const group of picker.querySelectorAll('select, fieldset')) {
    if (group instanceof HTMLSelectElement) {
      groups.push([...group.options].map((option) => ({ value: option.value, holder: option })));
      chosen.push(group.value.toLowerCase());
      continue;
    }
    const inputs = [...group.querySelectorAll('input[type="radio"]')].filter(
      (input) => input instanceof HTMLInputElement
    );
    groups.push(
      inputs.map((input) => ({
        value: input.value,
        holder: input.closest('label')?.querySelector('.variant-option__button-label__text') ?? null,
      }))
    );
    chosen.push((inputs.find((input) => input.checked)?.value ?? '').toLowerCase());
  }

  groups.forEach((group, index) => {
    for (const { value, holder } of group) {
      if (!(holder instanceof HTMLElement)) continue;
      const candidate = [...chosen];
      candidate[index] = value.toLowerCase();
      const isReady = label !== '' && combos.some((combo) => combo.values.every((wanted) => candidate.includes(wanted)));
      setReadyLabel(holder, isReady, label);
    }
  });
}

/**
 * @param {HTMLElement} holder - `<option>` del menu o testo del pulsante
 * @param {boolean} isReady
 * @param {string} label
 */
function setReadyLabel(holder, isReady, label) {
  if (holder instanceof HTMLOptionElement) {
    const original = holder.dataset.evgText ?? holder.textContent?.trim() ?? '';
    if (isReady) {
      holder.dataset.evgText = original;
      holder.textContent = `${original}${READY_SEPARATOR}${label}`;
    } else if (holder.dataset.evgText !== undefined) {
      holder.textContent = original;
      delete holder.dataset.evgText;
    }
    return;
  }

  const tag = holder.querySelector('.evg-ready-tag');
  if (isReady && !tag) {
    const span = document.createElement('span');
    span.className = 'evg-ready-tag';
    span.textContent = `${READY_SEPARATOR}${label}`;
    holder.append(span);
  } else if (!isReady && tag) {
    tag.remove();
  }
}

/**
 * Trova la misura: converte diametro, circonferenza (= misura EU) o misura US
 * nella misura del negozio e la seleziona nel variant picker.
 *
 * @typedef {{ option: string, values: { value: string, mm: number }[] }} SizeOption
 * @typedef {{ input: HTMLInputElement, unit: HTMLSelectElement, result: HTMLElement, apply: HTMLElement, sizes: HTMLScriptElement }} SizeFinderRefs
 * @extends {Component<SizeFinderRefs>}
 */
class EvgSizeFinder extends Component {
  requiredRefs = ['input', 'unit', 'result', 'apply', 'sizes'];

  /** @type {{ option: string, value: string } | null} */
  #match = null;

  /** @type {number | undefined} */
  #pending;

  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    super.connectedCallback();
    // Cambiando pietra sui pronti a partire cambia anche quali misure esistono: si ricalcola.
    this.#listeners = new AbortController();
    this.closest('.shopify-section, dialog')?.addEventListener(
      ThemeEvents.variantUpdate,
      () => {
        if (this.refs.input?.value) this.calculate();
      },
      { signal: this.#listeners.signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#pending);
    this.#listeners?.abort();
  }

  /**
   * Waits for the visitor to stop typing: "1" on the way to "16,5" is not an error.
   * @param {() => void} callback
   */
  #later(callback) {
    clearTimeout(this.#pending);
    this.#pending = window.setTimeout(callback, 700);
  }

  /** @returns {SizeOption[]} */
  get #sizeOptions() {
    try {
      return JSON.parse(this.refs.sizes.textContent || '[]');
    } catch (error) {
      return [];
    }
  }

  /**
   * Combinazioni dei pronti a partire, con la disponibilità.
   * @returns {{ options: string[], variants: [string[], number][] } | null}
   */
  get #ready() {
    if (!this.refs.ready) return null;
    try {
      return JSON.parse(this.refs.ready.textContent || 'null');
    } catch (error) {
      return null;
    }
  }

  /**
   * Opzioni scelte ora nel variant picker della scheda (radio "Nome-blocco-prodotto" o select "options[Nome]").
   * @returns {Record<string, string>}
   */
  #selectedOptions() {
    const picker = this.closest('.shopify-section, dialog')?.querySelector('variant-picker');
    /** @type {Record<string, string>} */
    const selected = {};
    if (!picker) return selected;
    for (const input of picker.querySelectorAll('input[type="radio"]:checked')) {
      if (input instanceof HTMLInputElement) selected[input.name.replace(/-[^-]+-\d+$/, '')] = input.value;
    }
    for (const select of picker.querySelectorAll('select')) {
      const name = select instanceof HTMLSelectElement ? select.name.match(/^options\[(.+)\]$/)?.[1] : null;
      if (name && select instanceof HTMLSelectElement) selected[name] = select.value;
    }
    return selected;
  }

  /**
   * Il valore di misura dei pronti con quel diametro, solo se esiste disponibile insieme alle altre opzioni scelte.
   * @param {number} mm
   * @param {SizeOption} sizeOption
   */
  #readyValue(mm, sizeOption) {
    const candidates = sizeOption.values.filter((size) => size.mm === mm);
    const ready = this.#ready;
    if (!ready || !candidates.length) return candidates[0] ?? null;
    const sizeIndex = ready.options.indexOf(sizeOption.option);
    const selected = this.#selectedOptions();
    const variant = ready.variants.find(
      ([values, available]) =>
        available &&
        candidates.some((size) => values[sizeIndex] === size.value) &&
        ready.options.every((name, index) => index === sizeIndex || !selected[name] || selected[name] === values[index])
    );
    return variant ? candidates.find((size) => size.value === variant[0][sizeIndex]) ?? null : null;
  }

  /**
   * Scala completa della scheda su misura, presente solo sui pronti a partire.
   * @returns {SizeOption['values'] | null}
   */
  get #scale() {
    if (!this.refs.scale) return null;
    try {
      const scale = JSON.parse(this.refs.scale.textContent || '[]');
      return Array.isArray(scale) && scale.length ? scale : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Converts the measurement typed by the visitor into an internal diameter in mm.
   * US: d = 11.63 + 0.8128 × misura · EU/circonferenza: d = C / π.
   * @returns {number | null}
   */
  #diameter() {
    const raw = Number(this.refs.input.value.replace(',', '.'));
    if (!raw) return null;

    switch (this.refs.unit.value) {
      case 'circumference':
        return raw / Math.PI;
      case 'us':
        return 11.63 + 0.8128 * raw;
      default:
        return raw;
    }
  }

  calculate() {
    const diameter = this.#diameter();
    const { result, apply } = this.refs;
    const [firstOption] = this.#sizeOptions;

    this.#match = null;
    apply.hidden = true;
    clearTimeout(this.#pending);

    if (!diameter || !firstOption?.values.length) {
      result.textContent = this.dataset.textEmpty ?? '';
      return;
    }

    const scale = this.#scale;
    const values = scale ?? firstOption.values;
    const smallest = values[0];
    const largest = values[values.length - 1];
    if (!smallest || !largest) return;

    if (diameter < smallest.mm - 0.6 || diameter > largest.mm + 0.6) {
      result.textContent = '';
      this.#later(() => {
        result.textContent = this.dataset.textOut ?? '';
        track('evg_size_out_of_range', { diameter: Math.round(diameter * 10) / 10 });
      });
      return;
    }

    let best = smallest;
    for (const size of values) {
      if (Math.abs(size.mm - diameter) < Math.abs(best.mm - diameter)) best = size;
    }

    // Pronti a partire: la misura giusta può non essere tra quelle in giacenza. Allora si rimanda al su misura.
    if (scale) {
      const ready = this.#readyValue(best.mm, firstOption);
      if (!ready) {
        result.textContent = (this.dataset.textMade ?? '').replace('[misura]', best.value);
        if (this.dataset.madeUrl) {
          const link = document.createElement('a');
          link.href = this.dataset.madeUrl;
          link.textContent = this.dataset.madeLabel ?? '';
          result.append(' ', link);
        }
        this.#later(() => track('evg_size_not_ready', { diameter: Math.round(diameter * 10) / 10, size: best.value }));
        return;
      }
      best = ready;
    }

    // Tra due misure: per fasce larghe conviene la più grande, lo diciamo invece di decidere noi.
    const larger = scale ? undefined : values.find((size) => size.mm > diameter);
    const isBetween = larger && larger !== best && Math.abs(best.mm - diameter) > 0.1;
    const message = (this.dataset.textResult ?? '').replace('[misura]', best.value);
    result.textContent = isBetween
      ? `${message} ${(this.dataset.textBetween ?? '').replace('[misura]', larger.value)}`
      : message;

    this.#match = { option: firstOption.option, value: best.value };
    apply.hidden = false;
    this.#later(() => track('evg_size_found', { diameter: Math.round(diameter * 10) / 10, size: best.value }));
  }

  /**
   * Selects the suggested size in every size option of the product (stacks have two).
   */
  applySize() {
    if (!this.#match) return;

    const section = this.closest('.shopify-section, dialog');
    const picker = section?.querySelector('variant-picker');
    if (!picker) return;

    const matchMm = this.#sizeOptions[0]?.values.find((size) => size.value === this.#match?.value)?.mm;

    for (const sizeOption of this.#sizeOptions) {
      const target = sizeOption.values.find((size) => size.mm === matchMm) ?? sizeOption.values.find((size) => size.value === this.#match?.value);
      if (!target) continue;

      const radio = [...picker.querySelectorAll('input[type="radio"]')].find(
        (input) =>
          input instanceof HTMLInputElement &&
          input.name.startsWith(`${sizeOption.option}-`) &&
          input.value === target.value
      );

      if (radio instanceof HTMLInputElement) {
        if (!radio.checked) radio.click();
        continue;
      }

      const select = [...picker.querySelectorAll('select')].find(
        (element) => element instanceof HTMLSelectElement && element.name === `options[${sizeOption.option}]`
      );

      if (select instanceof HTMLSelectElement) {
        select.value = target.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    track('evg_size_applied', { size: this.#match.value });
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    picker.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });
  }
}

/**
 * @param {Date} date
 * @param {number} days
 */
function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/**
 * Adds working days, skipping Saturdays, Sundays and Italian public holidays (same rule as Stack Studio).
 * @param {Date} date
 * @param {number} days
 */
function addBusinessDays(date, days) {
  let next = new Date(date);
  let added = 0;
  while (added < days) {
    next = addDays(next, 1);
    const weekday = next.getDay();
    if (weekday !== 0 && weekday !== 6 && !isHoliday(next)) added += 1;
  }
  return next;
}

const HOLIDAYS = ['1-1', '1-6', '4-25', '5-1', '6-2', '8-15', '11-1', '12-8', '12-25', '12-26'];

/** @param {Date} date */
function isHoliday(date) {
  if (HOLIDAYS.includes(`${date.getMonth() + 1}-${date.getDate()}`)) return true;
  // Lunedì dell'Angelo: Pasqua (algoritmo anonimo gregoriano) più un giorno.
  const year = date.getFullYear();
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const h = (19 * a + b - Math.floor(b / 4) - Math.floor((b - Math.floor((b + 8) / 25) + 1) / 3) + 15) % 30;
  const l = (32 + 2 * (b % 4) + 2 * Math.floor(c / 4) - h - (c % 4)) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const monday = new Date(year, Math.floor((h + l - 7 * m + 114) / 31) - 1, ((h + l - 7 * m + 114) % 31) + 2);
  return date.getMonth() === monday.getMonth() && date.getDate() === monday.getDate();
}

/**
 * Next occurrence of a weekday, strictly after the given date.
 * @param {Date} date
 * @param {number} weekday - 0 = domenica … 6 = sabato
 */
function nextWeekday(date, weekday) {
  const diff = (weekday - date.getDay() + 7) % 7 || 7;
  return addDays(date, diff);
}

// Clic sui contatti diretti (WhatsApp ecc.): un evento per misurare quanti partono dalla scheda.
if (!('evgTrackBound' in document.documentElement.dataset)) {
  document.documentElement.dataset.evgTrackBound = '';
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-evg-track]') : null;
    if (!(link instanceof HTMLElement)) return;
    track(link.dataset.evgTrack ?? 'evg_click', { path: location.pathname });
  });
}

if (!customElements.get('evg-variant-sync')) {
  customElements.define('evg-variant-sync', EvgVariantSync);
}

if (!customElements.get('evg-delivery-promise')) {
  customElements.define('evg-delivery-promise', EvgDeliveryPromise);
}

if (!customElements.get('evg-size-finder')) {
  customElements.define('evg-size-finder', EvgSizeFinder);
}
